import {
  Add as AddIcon,
  Email as EmailIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddClientModal from '../components/AddClientModal';
import CustomDataGrid from '../components/CustomDataGrid';
import Loading from '../components/Loading';
import { useDietitianData } from '../hooks/useDietitianData';
import { Client, clientApi } from '../services/clientApi';
import { colors } from '../theme';

const DietitianClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const { clients, loading, setClients, showSuccess, showError, showWarning } =
    useDietitianData({
      fetchClients: true,
      fetchAppointments: false,
      fetchDietPrograms: false,
    });

  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );

  // Filter clients based on search query and column filters
  const filteredClients = clients.filter((client) => {
    const query = searchQuery.toLowerCase();
    const matchesSearch =
      !searchQuery ||
      client.firstName.toLowerCase().includes(query) ||
      client.lastName.toLowerCase().includes(query) ||
      client.email.toLowerCase().includes(query) ||
      client.phone?.toLowerCase().includes(query) ||
      client.goal?.toLowerCase().includes(query);

    if (!matchesSearch) return false;

    // Column filters
    for (const [field, filterValue] of Object.entries(columnFilters)) {
      if (!filterValue) continue;

      const value = String(
        field === 'fullName'
          ? `${client.firstName} ${client.lastName}`
          : client[field as keyof Client] || ''
      ).toLowerCase();

      if (!value.includes(filterValue.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  const handleColumnFilter = (field: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isMobile = useMediaQuery('(max-width: 600px)');

  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleAddClient = async (
    clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const tempPassword = generateTempPassword();

      await clientApi.createClient(clientData).then(async (res) => {
        setClients((prev) => [...prev, res.client]);

        try {
          const { createUserWithEmailAndPassword } = await import(
            'firebase/auth'
          );
          const { auth } = await import('../firebase');

          await createUserWithEmailAndPassword(
            auth,
            clientData.email,
            tempPassword
          );

          const setPasswordUrl = `${
            window.location.origin
          }/client/set-password?email=${encodeURIComponent(
            clientData.email
          )}&temp=${tempPassword}`;

          try {
            const { emailApi } = await import('../services/emailApi');
            const result = await emailApi.sendInvitationEmail(
              clientData.email,
              `${clientData.firstName} ${clientData.lastName}`,
              tempPassword,
              setPasswordUrl
            );

            if (result.warning) {
              showWarning(
                `${clientData.firstName} ${clientData.lastName} başarıyla eklendi ancak davet email'i gönderilemedi.`,
                'Kısmi Başarı'
              );
            } else {
              showSuccess(
                `${clientData.firstName} ${clientData.lastName} başarıyla eklendi!`,
                'Danışan Eklendi'
              );
            }
          } catch (emailError) {
            showWarning(
              `${clientData.firstName} ${clientData.lastName} başarıyla eklendi ancak email gönderilemedi.`,
              'Kısmi Başarı'
            );
          }
        } catch (authError: any) {
          showWarning(
            `Danışan eklendi ancak giriş bilgileri oluşturulamadı.`,
            'Kısmi Başarı'
          );
        }
      });
      setIsAddClientModalOpen(false);
    } catch (err) {
      showError(
        'Danışan eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
      console.error('Error creating client:', err);
    }
  };

  // DataGrid columns
  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: 'Ad Soyad',
      flex: 1.5,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: colors.primary.main,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.1rem',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
            }}
          >
            {params.row.firstName?.charAt(0)}
            {params.row.lastName?.charAt(0)}
          </Box>
          <Typography variant="body1" fontWeight={600} fontSize="1rem">
            {params.row.firstName} {params.row.lastName}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      minWidth: 240,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EmailIcon sx={{ fontSize: 20, color: colors.text.secondary }} />
          <Typography variant="body2" fontSize="0.95rem">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Telefon',
      flex: 1,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {params.value ? (
            <>
              <PhoneIcon sx={{ fontSize: 20, color: colors.text.secondary }} />
              <Typography variant="body2" fontSize="0.95rem">
                {params.value}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'age',
      headerName: 'Yaş',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value || '-'}
          sx={{
            bgcolor: colors.background.light,
            fontWeight: 600,
            fontSize: '0.9rem',
            height: 36,
            px: 1,
          }}
        />
      ),
    },
    {
      field: 'goal',
      headerName: 'Hedef',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip
            label={params.value}
            sx={{
              bgcolor: colors.chip.primaryBg,
              color: colors.primary.main,
              fontWeight: 600,
              fontSize: '0.9rem',
              border: `1px solid ${colors.primary.light}`,
              height: 36,
              px: 1,
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Kayıt Tarihi',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.95rem">
          {params.value
            ? new Date(params.value).toLocaleDateString('tr-TR')
            : '-'}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Danışanlar yükleniyor..."
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 24px)',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            bgcolor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            mb: 3,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flex={1}
              minWidth={0}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: colors.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <PeopleIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box minWidth={0} flex={1}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: colors.text.primary }}
                  noWrap
                >
                  Danışanlarım
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {filteredClients.length} / {clients.length} danışan
                </Typography>
              </Box>
            </Box>
            {!isMobile && (
              <Button
                variant="contained"
                onClick={() => setIsAddClientModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  width: 300,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(46, 125, 50, 0.3)',
                  ml: 2,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    boxShadow: '0 6px 16px rgba(46, 125, 50, 0.4)',
                  },
                }}
              >
                Yeni Danışan Ekle
              </Button>
            )}
            {isMobile && (
              <IconButton
                onClick={() => setIsAddClientModalOpen(true)}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  width: 36,
                  height: 36,
                  ml: 1,
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: colors.hover.primary,
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Danışan ara (isim, email, telefon, hedef...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.background.light,
                borderRadius: 2,
                '&:hover': {
                  bgcolor: 'white',
                },
                '&.Mui-focused': {
                  bgcolor: 'white',
                },
              },
            }}
          />
        </Box>

        {/* DataGrid */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <CustomDataGrid
            rows={filteredClients}
            columns={columns}
            loading={loading}
            onRowClick={(params) =>
              navigate(`/dietitian/clients/${params.row.id}`)
            }
            onColumnFilter={handleColumnFilter}
            noRowsLabel="Henüz danışan bulunmuyor"
          />
        </Box>
      </Box>

      <AddClientModal
        open={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onSave={handleAddClient}
      />
    </>
  );
};

export default DietitianClientsPage;
